import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { FilterStateService } from '@services/filter-state.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { IconSanitizerService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { AccountFormEditorComponent } from '../form-editors/account-form-editor/account-form-editor.component';
import { AccountTypeFormEditorComponent } from '../form-editors/account-type-form-editor/account-type-form-editor.component';
import { CityFormEditorComponent } from '../form-editors/city-form-editor/city-form-editor.component';
import { CompaniesFormEditorComponent } from '../form-editors/companies-form-editor/companies-form-editor.component';
import { CompanyMainContactFormEditorComponent } from '../form-editors/company-main-contact-form-editor/company-main-contact-form-editor.component';
import { CompanyRolesFormEditorComponent } from '../form-editors/company-roles-form-editor/company-roles-form-editor.component';
import { CompanyTypeFormEditorComponent } from '../form-editors/company-type-form-editor/company-type-form-editor.component';
import { CountryFormEditorComponent } from '../form-editors/country-form-editor/country-form-editor.component';
import { DateRangeFormEditorComponent } from '../form-editors/date-range-form-editor/date-range-form-editor.component';
import { DepartmentFormEditorComponent } from '../form-editors/department-form-editor/department-form-editor.component';
import { DropdownRangesFormEditorComponent } from '../form-editors/dropdown-ranges-form-editor/dropdown-ranges-form-editor.component';
import { IncludeArchivedFormEditorComponent } from '../form-editors/include-archived-form-editor/include-archived-form-editor.component';
import { MarketFormEditorComponent } from '../form-editors/market-form-editor/market-form-editor.component';
import { SalespersonsFormEditorComponent } from '../form-editors/salespersons-form-editor/salespersons-form-editor.component';
import { StateFormEditorComponent } from '../form-editors/state-form-editor/state-form-editor.component';
import { StatusesFormEditorComponent } from '../form-editors/statuses-form-editor/statuses-form-editor.component';
import { GenericOverlayComponent } from '../generic-overlay/generic-overlay.component';
import {
    IAdditionalFiltersConfig,
    IAdditionalFilterVisibilityConfig,
    IGenericAdditionalFilters,
    IReadonlyFields
} from './additional-filters.config';

@Component({
    selector: 'app-additional-filters',
    standalone: true,
    imports: [
        CommonModule,
        ...CDK_IMPORTS,
        GenericOverlayComponent,
        ReactiveFormsModule,
        DateRangeFormEditorComponent,
        AccountFormEditorComponent,
        CityFormEditorComponent,
        CompaniesFormEditorComponent,
        CompanyRolesFormEditorComponent,
        CountryFormEditorComponent,
        DropdownRangesFormEditorComponent,
        DepartmentFormEditorComponent,
        SalespersonsFormEditorComponent,
        StateFormEditorComponent,
        StatusesFormEditorComponent,
        MarketFormEditorComponent,
        CompanyTypeFormEditorComponent,
        CompanyMainContactFormEditorComponent,
        AccountTypeFormEditorComponent,
        IncludeArchivedFormEditorComponent
    ],
    templateUrl: './additional-filters.component.html',
    styleUrls: ['./additional-filters.component.scss']
})
export class AdditionalFiltersComponent<T extends IGenericAdditionalFilters = IGenericAdditionalFilters>
    implements OnInit, OnDestroy
{
    config = input.required<IAdditionalFiltersConfig<T>>();

    readonlyFields = input<IReadonlyFields>();

    filtersVisibility = computed<IAdditionalFilterVisibilityConfig>(() => {
        const config = this.config();
        return config.visibility;
    });

    opened = computed(() => this.overlayOpenState());

    hasAppliedFilters = computed(() => {
        const filters = this.currentFilters();
        return this.config().hasFiltersApplied(filters);
    });
    additionalFiltersForm: FormGroup = new FormGroup({});

    hasSelectedFilters = signal<boolean>(false);

    // Icons
    closeIcon?: SafeHtml;
    additionalFiltersIcon?: SafeHtml;
    appliedAdditionalFiltersIcon?: SafeHtml;

    private readonly overlayOpenState = signal<boolean>(false);
    private readonly currentFilters = signal<T | null>(null);
    private filtersSub?: Subscription;

    private readonly filterStateService = inject(FilterStateService<any, T>);
    private readonly iconSanitizer = inject(IconSanitizerService);

    ngOnInit(): void {
        this.closeIcon = this.iconSanitizer.getIcon('close');
        this.additionalFiltersIcon = this.iconSanitizer.getIcon('additionalFilters');
        this.appliedAdditionalFiltersIcon = this.iconSanitizer.getIcon('appliedAdditionalFilters');
        this.initializeForm();

        this.filtersSub = this.filterStateService.stateFilters$.subscribe((filters) => {
            this.currentFilters.set(filters);
            this.updateFormFromFilters(filters);
        });

        this.additionalFiltersForm.valueChanges.subscribe((controlValues) => {
            this.hasSelectedFilters.set(this.checkSelectedFilters(controlValues));
        });
    }

    checkSelectedFilters(controlValues: any): boolean {
        return Object.values(controlValues).some((controlVal) => {
            if (!controlVal) return;

            // array control
            if (Array.isArray(controlVal)) {
                return !!controlVal.length;
            }

            // range control
            if (typeof controlVal === 'object' && 'from' in controlVal && 'to' in controlVal) {
                return controlVal.from || controlVal.to;
            }

            return controlVal;
        });
    }

    ngOnDestroy(): void {
        this.filtersSub?.unsubscribe();
    }

    onOverlayChange(isOpen: boolean): void {
        this.overlayOpenState.set(isOpen);
    }

    onClearFilters(): void {
        const allowSubmit = this.hasAppliedFilters();
        const config = this.config();
        const emptyFormValue = config.transformFromFilters({} as any);
        this.additionalFiltersForm.reset(emptyFormValue);

        const clearedFilters = config.transformToFilters(emptyFormValue);
        this.currentFilters.set(clearedFilters as T);
        if (!allowSubmit) return;

        this.onSubmit();
    }

    onSubmit(): void {
        const formValue = this.additionalFiltersForm.value;
        const config = this.config();

        const additionalFilters = config.transformToFilters(formValue);
        this.filterStateService.updateAdditionalFilters(additionalFilters);
    }

    private initializeForm(): void {
        const cfg = this.config();
        const initialFormValue = cfg.transformFromFilters(this.filterStateService.current as T);
        const formControls: Record<string, FormControl> = {};
        Object.keys(initialFormValue || {}).forEach((key) => {
            formControls[key] = new FormControl(initialFormValue[key]);
        });
        this.additionalFiltersForm = new FormGroup(formControls);
    }

    private updateFormFromFilters(filters: T): void {
        const config = this.config();
        const formValue = config.transformFromFilters(filters);
        this.additionalFiltersForm.patchValue(formValue);
    }
}
